// One activity event as a sentence: actor, verb, object. The server's summary is the verb phrase ("asked for review
// of", "edited"); the object is the pages it names, which the summary leaves out. Both the Activity tab and the site
// page's Recent activity build their line from this, so an event never reads as a sentence with its object dropped.

export interface ActivityLike { summary: string; page_paths: readonly string[] }

/** The pages an event is about, as words: up to three names, else "N pages". Empty when the summary already says it. */
export function activityObject(e: ActivityLike, name: (path: string) => string): string[] | string | null {
  const pages = e.page_paths ?? [];
  if (!pages.length || /\bpages?\s*$/i.test(e.summary.trim())) return null;
  if (pages.length <= 3) return pages.map(name);
  return `${pages.length} pages`;
}

/**
 * The verb phrase of a summary. The activity API (bloxx-analytics-mcp activityFeed.ts) sends whole sentences that start
 * with the actor's name ("Charlie cancelled …", "Claude (for Charlie) edited …"); every line puts the actor in front
 * itself, so a leading name (and an agent's "(for …)") is taken off. A verb-only summary is returned as it is.
 */
export function activityVerb(actor: string, summary: string): string {
  const s = (summary ?? "").trim(), a = (actor ?? "").trim();
  if (!a || !s.startsWith(a)) return s;
  const rest = s.slice(a.length), lead = /^(?:\s+\([^)]*\))?\s+(?=\S)/.exec(rest);
  return lead ? rest.slice(lead[0].length) : s;
}

/** The whole line as plain text (for titles and tests). */
export function activitySentence(actor: string, e: ActivityLike, name: (path: string) => string): string {
  const object = activityObject(e, name);
  const words = Array.isArray(object) ? object.join(", ") : object;
  return [actor, activityVerb(actor, e.summary), words].filter(Boolean).join(" ");
}
